import React, { useEffect, useState } from "react";
import { keyframes, ToggleButton, Tooltip, Box, Typography } from "@mui/material";
import MicIcon from "@mui/icons-material/MicRounded";
import StopIcon from "@mui/icons-material/StopRounded";
import { useRealtimeVoice } from "../hooks/useRealtimeVoice";

// Create keyframes for the pulsing animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const speakingPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.8;
  }
`;

export interface VoiceIORealtimeProps {
  assistantId: string;
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
  enableFunctions?: boolean;
  instructions?: string;
  onTranscript?: (text: string) => void;
}

/**
 * VoiceIORealtime - Low-latency voice conversations using OpenAI Realtime API
 * 
 * Features:
 * - < 300ms latency (10x faster than traditional approach)
 * - Natural interruptions (can interrupt AI mid-sentence)
 * - Real-time transcripts
 * - Function calling during voice conversations
 * - Bi-directional audio streaming
 * 
 * Usage:
 * <VoiceIORealtime 
 *   assistantId={assistant.id} 
 *   voice="alloy"
 *   enableFunctions={true}
 * />
 */
function VoiceIORealtime({
  assistantId,
  voice = 'alloy',
  enableFunctions = true,
  instructions,
  onTranscript,
}: VoiceIORealtimeProps) {
  
  const {
    isConnected,
    isRecording,
    isSpeaking,
    transcript,
    status,
    error,
    startSession,
    startRecording,
    stopRecording,
    interrupt,
    endSession,
  } = useRealtimeVoice();

  const [sessionStarted, setSessionStarted] = useState(false);

  // Start session when component mounts and assistant ID is available
  useEffect(() => {
    if (isConnected && assistantId && !sessionStarted) {
      console.log('🎙️ Starting Realtime session for assistant:', assistantId);
      
      startSession({
        assistantId,
        voice,
        instructions,
        enableFunctions,
      });
      
      setSessionStarted(true);
    }
  }, [isConnected, assistantId, sessionStarted, startSession, voice, instructions, enableFunctions]);

  // Notify parent of transcripts
  useEffect(() => {
    if (transcript && onTranscript) {
      onTranscript(transcript);
    }
  }, [transcript, onTranscript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionStarted) {
        endSession();
      }
    };
  }, [sessionStarted, endSession]);

  const handleToggleRecording = () => {
    if (isSpeaking) {
      // Interrupt AI if it's speaking
      interrupt();
    } else if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const getButtonColor = () => {
    if (isSpeaking) return "warning"; // AI is speaking
    if (isRecording) return "secondary"; // User is recording
    return "primary"; // Idle
  };

  const getButtonTooltip = () => {
    if (!isConnected) return "Connecting to voice service...";
    if (!sessionStarted) return "Starting session...";
    if (isSpeaking) return "AI is speaking - click to interrupt";
    if (isRecording) return "Recording - click to stop";
    return "Click to start talking";
  };

  const getStatusIcon = () => {
    if (isSpeaking) return <StopIcon />;
    return <MicIcon />;
  };

  return (
    <div className="chat-reset-section">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={getButtonTooltip()} arrow>
          <ToggleButton
            selected={isRecording || isSpeaking}
            value="recording"
            color={getButtonColor()}
            aria-label={isRecording ? "Stop recording" : "Start recording"}
            className={`mic-icon ${isRecording ? "mic-button-listening" : ""} ${isSpeaking ? "mic-button-speaking" : ""}`}
            onChange={handleToggleRecording}
            disabled={!isConnected || !sessionStarted}
            sx={{
              position: "relative",
              background: !isConnected 
                ? 'rgba(128, 128, 128, 0.2)'
                : 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '12px',
              color: !isConnected 
                ? 'rgba(255, 255, 255, 0.5)'
                : 'rgba(255, 255, 255, 0.85)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
              transition: 'background 0.3s ease, transform 0.2s ease',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.2)',
                transform: isConnected ? 'scale(1.08)' : 'none',
              },
              '& svg': {
                zIndex: 1,
                position: 'relative',
              },
              // Recording animation
              "&::before": isRecording
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    margin: "auto",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    backgroundColor: "rgba(0, 123, 255, 0.5)",
                    animation: `${pulse} 1.5s infinite ease-out`,
                    zIndex: 0,
                  }
                : {},
              // Speaking animation
              "&::after": isSpeaking
                ? {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    margin: "auto",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 165, 0, 0.5)",
                    animation: `${speakingPulse} 1s infinite ease-in-out`,
                    zIndex: 0,
                  }
                : {},
            }}
          >
            {getStatusIcon()}
          </ToggleButton>
        </Tooltip>

        {/* Status indicator */}
        {(transcript || status !== 'Ready') && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 150,
          }}>
            {transcript && (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontStyle: 'italic',
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                "{transcript}"
              </Typography>
            )}
            <Typography 
              variant="caption" 
              sx={{ 
                color: isSpeaking 
                  ? 'rgba(255, 165, 0, 0.9)' 
                  : isRecording 
                    ? 'rgba(0, 123, 255, 0.9)'
                    : 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.7rem',
              }}
            >
              {status}
            </Typography>
          </Box>
        )}

        {/* Error indicator */}
        {error && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 0, 0, 0.9)',
              fontSize: '0.7rem',
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </div>
  );
}

export default VoiceIORealtime;
